/**
 * Role-based routing utilities
 * Provides centralized role-to-dashboard mapping for consistent routing
 */

import type { Role } from '../types/auth';

/**
 * Dashboard routes for each role
 */
export const ROLE_DASHBOARDS: Partial<Record<Role, string>> = {
  SUPER_ADMIN: '/admin',
  ADMIN: '/admin',
  TECHNICAL_ADMIN: '/integration-keys',
  REGISTERED_MANAGER: '/analytics/admin',
  DEPUTY_MANAGER: '/analytics/admin',
  CARE_COORDINATOR: '/analytics/coordinator',
  CARE_SUPERVISOR: '/live-carers',
  ACCOUNTANT: '/analytics/reports',
  RECRUITMENT_AND_HR_OFFICER: '/caregivers',
  COORDINATOR: '/analytics/coordinator',
  MANAGER: '/analytics/admin',
  CARER: '/caregiver',
  CAREGIVER: '/caregiver',
  CANDIDATE: '/candidate-exam',
  FAMILY: '/family-portal',
};

/**
 * Get the appropriate dashboard route for a user based on their roles
 * Uses priority-based role detection
 */
export function getDashboardRoute(roles: Role[]): string {
  if (roles.includes('SUPER_ADMIN')) return '/admin';
  if (roles.includes('ADMIN')) return '/admin';
  if (roles.includes('TECHNICAL_ADMIN')) return '/integration-keys';
  if (roles.includes('REGISTERED_MANAGER')) return '/analytics/admin';
  if (roles.includes('DEPUTY_MANAGER')) return '/analytics/admin';
  if (roles.includes('CARE_COORDINATOR')) return '/analytics/coordinator';
  if (roles.includes('CARE_SUPERVISOR')) return '/live-carers';
  if (roles.includes('ACCOUNTANT')) return '/analytics/reports';
  if (roles.includes('RECRUITMENT_AND_HR_OFFICER')) return '/caregivers';
  if (roles.includes('COORDINATOR')) return '/analytics/coordinator';
  if (roles.includes('MANAGER')) return '/analytics/admin';
  if (roles.includes('CARER')) return '/caregiver';
  if (roles.includes('CAREGIVER')) return '/caregiver';
  if (roles.includes('CANDIDATE')) return '/candidate-exam';
  if (roles.includes('FAMILY')) return '/family-portal';
  return '/';
}

/**
 * Check if a user should be redirected based on their role and current path
 */
export function shouldRedirect(roles: Role[], currentPath: string): boolean {
  if (currentPath === '/login') {
    return false;
  }
  if (roles.includes('SUPER_ADMIN')) return false;
  if (roles.includes('ADMIN')) {
    return currentPath.startsWith('/enterprise-readiness') ||
      currentPath.startsWith('/gp-connect') ||
      currentPath.startsWith('/family-portal') ||
      currentPath.startsWith('/caregiver');
  }
  if (roles.includes('TECHNICAL_ADMIN')) {
    return !currentPath.startsWith('/integration-keys') &&
      !currentPath.startsWith('/assistant-ai-control') &&
      !currentPath.startsWith('/owner-activity') &&
      !currentPath.startsWith('/enterprise-readiness') &&
      !currentPath.startsWith('/workflow-builder') &&
      !currentPath.startsWith('/gp-connect') &&
      !currentPath.startsWith('/security-enterprise') &&
      !currentPath.startsWith('/settings');
  }
  if (roles.includes('REGISTERED_MANAGER') || roles.includes('DEPUTY_MANAGER')) {
    return currentPath.startsWith('/security-enterprise') ||
      currentPath.startsWith('/integration-keys') ||
      currentPath.startsWith('/gp-connect') ||
      currentPath.startsWith('/workflow-builder') ||
      currentPath.startsWith('/roles-access') ||
      currentPath.startsWith('/enterprise-readiness') ||
      currentPath.startsWith('/family-portal') ||
      currentPath.startsWith('/caregiver');
  }
  if (roles.includes('CARE_COORDINATOR')) {
    return !currentPath.startsWith('/analytics/coordinator') &&
      !currentPath.startsWith('/clients') &&
      !currentPath.startsWith('/caregivers') &&
      !currentPath.startsWith('/scheduling') &&
      !currentPath.startsWith('/visits') &&
      !currentPath.startsWith('/care-plans') &&
      !currentPath.startsWith('/tasks') &&
      !currentPath.startsWith('/time-tracking') &&
      !currentPath.startsWith('/incidents') &&
      !currentPath.startsWith('/live-carers') &&
      !currentPath.startsWith('/admin-ai') &&
      !currentPath.startsWith('/settings');
  }
  if (roles.includes('CARE_SUPERVISOR')) {
    return !currentPath.startsWith('/live-carers') &&
      !currentPath.startsWith('/caregivers') &&
      !currentPath.startsWith('/visits') &&
      !currentPath.startsWith('/care-plans') &&
      !currentPath.startsWith('/tasks') &&
      !currentPath.startsWith('/time-tracking') &&
      !currentPath.startsWith('/incidents') &&
      !currentPath.startsWith('/quality-assurance') &&
      !currentPath.startsWith('/compliance') &&
      !currentPath.startsWith('/settings');
  }
  if (roles.includes('ACCOUNTANT')) {
    return !currentPath.startsWith('/analytics/reports') &&
      !currentPath.startsWith('/billing') &&
      !currentPath.startsWith('/payroll') &&
      !currentPath.startsWith('/carer-payroll') &&
      !currentPath.startsWith('/clients') &&
      !currentPath.startsWith('/caregivers') &&
      !currentPath.startsWith('/visits') &&
      !currentPath.startsWith('/settings');
  }
  if (roles.includes('RECRUITMENT_AND_HR_OFFICER')) {
    return !currentPath.startsWith('/caregivers') &&
      !currentPath.startsWith('/documents') &&
      !currentPath.startsWith('/quality-assurance') &&
      !currentPath.startsWith('/compliance') &&
      !currentPath.startsWith('/analytics/reports') &&
      !currentPath.startsWith('/settings');
  }
  if (roles.includes('COORDINATOR')) {
    return !currentPath.startsWith('/analytics/coordinator') &&
      !currentPath.startsWith('/clients') &&
      !currentPath.startsWith('/caregivers') &&
      !currentPath.startsWith('/scheduling') &&
      !currentPath.startsWith('/visits') &&
      !currentPath.startsWith('/care-plans') &&
      !currentPath.startsWith('/tasks') &&
      !currentPath.startsWith('/time-tracking') &&
      !currentPath.startsWith('/incidents') &&
      !currentPath.startsWith('/settings');
  }
  if (roles.includes('MANAGER')) {
    return currentPath.startsWith('/security-enterprise') ||
      currentPath.startsWith('/integration-keys') ||
      currentPath.startsWith('/gp-connect') ||
      currentPath.startsWith('/workflow-builder') ||
      currentPath.startsWith('/family-portal') ||
      currentPath.startsWith('/caregiver');
  }
  if (roles.includes('CARER') || roles.includes('CAREGIVER')) {
    return !currentPath.startsWith('/caregiver') &&
      !currentPath.startsWith('/tasks') &&
      !currentPath.startsWith('/time-tracking') &&
      !currentPath.startsWith('/settings');
  }
  if (roles.includes('CANDIDATE')) {
    return !currentPath.startsWith('/candidate-exam');
  }
  if (roles.includes('FAMILY')) {
    return !currentPath.startsWith('/family-portal');
  }
  return true;
}

/**
 * Get a user-friendly role label
 */
export function getRoleLabel(role: Role): string {
  if (role === 'SUPER_ADMIN') return 'System Owner';
  if (role === 'ADMIN') return 'Administrator';
  if (role === 'TECHNICAL_ADMIN') return 'Technical Admin';
  if (role === 'REGISTERED_MANAGER') return 'Registered Manager';
  if (role === 'DEPUTY_MANAGER') return 'Deputy Manager';
  if (role === 'CARE_COORDINATOR') return 'Care Coordinator';
  if (role === 'CARE_SUPERVISOR') return 'Care Supervisor';
  if (role === 'ACCOUNTANT') return 'Accountant';
  if (role === 'RECRUITMENT_AND_HR_OFFICER') return 'Recruitment and HR Officer';
  if (role === 'COORDINATOR') return 'Coordinator';
  if (role === 'MANAGER') return 'Manager';
  if (role === 'CARER' || role === 'CAREGIVER') return 'Carer';
  if (role === 'CANDIDATE') return 'Candidate';
  if (role === 'FAMILY') return 'Family Member';
  return String(role).split('_').map((part) => part.charAt(0) + part.slice(1).toLowerCase()).join(' ');
}

/**
 * Get the primary role to display for a user
 * Returns the highest priority role
 */
export function getPrimaryRole(roles: Role[]): Role | null {
  if (roles.includes('SUPER_ADMIN')) return 'SUPER_ADMIN';
  if (roles.includes('ADMIN')) return 'ADMIN';
  if (roles.includes('TECHNICAL_ADMIN')) return 'TECHNICAL_ADMIN';
  if (roles.includes('REGISTERED_MANAGER')) return 'REGISTERED_MANAGER';
  if (roles.includes('DEPUTY_MANAGER')) return 'DEPUTY_MANAGER';
  if (roles.includes('CARE_COORDINATOR')) return 'CARE_COORDINATOR';
  if (roles.includes('CARE_SUPERVISOR')) return 'CARE_SUPERVISOR';
  if (roles.includes('ACCOUNTANT')) return 'ACCOUNTANT';
  if (roles.includes('RECRUITMENT_AND_HR_OFFICER')) return 'RECRUITMENT_AND_HR_OFFICER';
  if (roles.includes('COORDINATOR')) return 'COORDINATOR';
  if (roles.includes('MANAGER')) return 'MANAGER';
  if (roles.includes('CARER')) return 'CARER';
  if (roles.includes('CAREGIVER')) return 'CAREGIVER';
  if (roles.includes('CANDIDATE')) return 'CANDIDATE';
  if (roles.includes('FAMILY')) return 'FAMILY';
  return null;
}
